import { rootApi } from '@components/rootApi';
import {
  FlexibleReportRequestResponse,
  flexibleReportRequestResponse,
  flexibleReportRequestsResponse,
  FlexibleReportRequestsResponse,
  FlexibleReportRequestUpdateParams,
  dmasResponse,
  DmasResponse,
  locationListsResponse,
  LocationListsResponse,
} from '../schema';
import { FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import { FLEXIBLE_REPORT_REQUEST_TAG } from './tags';

export type GetFlexibleReportRequestsParams = {
  campaignId: number;
  page?: number;
  per_page?: number;
  sort_key?:
    | 'name'
    | 'start_date'
    | 'end_date'
    | 'location_count'
    | 'created_at'
    | 'status';
  sort_direction?: 'asc' | 'desc';
  search_text?: string;
};

const transformResponseId = (_response: unknown, meta: FetchBaseQueryMeta) => {
  if (!meta.response) throw new Error('No response');

  const locationHeader = meta.response.headers.get('Location');
  if (!locationHeader) throw new Error('No Location header');

  const id = locationHeader.split('/').at(-1);
  if (!id) throw new Error('No id in Location header');

  return Number.parseInt(id);
};

const pathWithBaseUrl = (path: string) => `/api/v3/campaigns/${path}`;

const reportsApi = rootApi.injectEndpoints({
  endpoints: builder => ({
    getFlexibleReportRequests: builder.query<
      FlexibleReportRequestsResponse,
      GetFlexibleReportRequestsParams
    >({
      query: ({ campaignId, ...params }) => ({
        url: pathWithBaseUrl(`${campaignId}/flexible_report_requests`),
        params,
      }),
      transformResponse: (response: FlexibleReportRequestsResponse) =>
        flexibleReportRequestsResponse.parse(response),
      providesTags: response =>
        response
          ? response.data.flexibleReportRequests.map(({ id }) => ({
              type: FLEXIBLE_REPORT_REQUEST_TAG,
              id,
            }))
          : [],
      // Avoid caching data due to real-time nature and form updates
      keepUnusedDataFor: 0,
    }),

    getFlexibleReportRequest: builder.query<
      FlexibleReportRequestResponse,
      { campaignId: number; flexibleReportRequestId: number }
    >({
      query: ({ campaignId, flexibleReportRequestId }) => ({
        url: pathWithBaseUrl(
          `${campaignId}/flexible_report_requests/${flexibleReportRequestId}`
        ),
      }),
      transformResponse: (response: FlexibleReportRequestResponse) =>
        flexibleReportRequestResponse.parse(response),
      providesTags: response =>
        response
          ? [
              {
                type: FLEXIBLE_REPORT_REQUEST_TAG,
                id: response.data.flexibleReportRequest.id,
              },
            ]
          : [],
      // Avoid caching data due to real-time nature and form updates
      keepUnusedDataFor: 0,
    }),

    createFlexibleReportRequest: builder.mutation<
      number,
      { campaignId: number }
    >({
      query: ({ campaignId }) => ({
        url: pathWithBaseUrl(`${campaignId}/flexible_report_requests`),
        method: 'POST',
      }),
      transformResponse: transformResponseId,
      invalidatesTags: [FLEXIBLE_REPORT_REQUEST_TAG],
    }),

    updateFlexibleReportRequest: builder.mutation<
      FlexibleReportRequestResponse,
      {
        campaignId: number;
        flexibleReportRequestId: number;
        body: FlexibleReportRequestUpdateParams;
      }
    >({
      query: ({ campaignId, flexibleReportRequestId, body }) => ({
        url: pathWithBaseUrl(
          `${campaignId}/flexible_report_requests/${flexibleReportRequestId}`
        ),
        body,
        method: 'PATCH',
      }),
    }),

    deleteFlexibleReportRequest: builder.mutation<
      FlexibleReportRequestResponse,
      { campaignId: number; flexibleReportRequestId: number }
    >({
      query: ({ campaignId, flexibleReportRequestId }) => ({
        url: pathWithBaseUrl(
          `${campaignId}/flexible_report_requests/${flexibleReportRequestId}`
        ),
        method: 'DELETE',
      }),
      invalidatesTags: [FLEXIBLE_REPORT_REQUEST_TAG],
    }),

    duplicateFlexibleReportRequest: builder.mutation<
      number,
      { campaignId: number; flexibleReportRequestId: number }
    >({
      query: ({ campaignId, flexibleReportRequestId }) => ({
        url: pathWithBaseUrl(
          `${campaignId}/flexible_report_requests/${flexibleReportRequestId}/duplicate`
        ),
        method: 'POST',
        transformResponse: transformResponseId,
      }),
      invalidatesTags: [FLEXIBLE_REPORT_REQUEST_TAG],
    }),

    submitFlexibleReportRequest: builder.mutation<
      FlexibleReportRequestResponse,
      { campaignId: number; flexibleReportRequestId: number }
    >({
      query: ({ campaignId, flexibleReportRequestId }) => ({
        url: pathWithBaseUrl(
          `${campaignId}/flexible_report_requests/${flexibleReportRequestId}/submit`
        ),
        method: 'POST',
      }),
    }),

    getLocationLists: builder.query<
      LocationListsResponse,
      { campaignId: number }
    >({
      query: ({ campaignId }) => ({
        url: `/api/campaigns/${campaignId}/location_lists`,
      }),
      transformResponse: (response: LocationListsResponse) =>
        locationListsResponse.parse(response),
    }),

    getDmas: builder.query<DmasResponse, { locationListId: number }>({
      query: ({ locationListId }) => ({
        url: `/api/location_lists/${locationListId}/dmas`,
      }),
      transformResponse: (response: DmasResponse) =>
        dmasResponse.parse(response),
    }),
  }),
});

export const {
  useGetFlexibleReportRequestsQuery,
  useGetFlexibleReportRequestQuery,
  useCreateFlexibleReportRequestMutation,
  useUpdateFlexibleReportRequestMutation,
  useDeleteFlexibleReportRequestMutation,
  useDuplicateFlexibleReportRequestMutation,
  useSubmitFlexibleReportRequestMutation,
  useGetLocationListsQuery,
  useGetDmasQuery,
} = reportsApi;
